import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from '../Common.module.css';
import { 
    CalendarIcon, 
    ClockIcon, 
    InstagramIcon, 
    PinterestIcon, 
    PrintIcon,
} from '../Icons';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { blogPosts, BlogPost } from '../../data/blogPosts';
import { AuthorBio } from '../AuthorBio';
import { ContactForm } from '../ContactForm';
import { ShareButtons } from '../ShareButtons';
import { generateToc } from '../utils/toc';
import { TableOfContents } from '../TableOfContents';
import { RecipeRating } from '../RecipeRating';
import { PinItButton } from '../PinItButton';
import { PremiumPromo } from '../PremiumPromo';
import { AdUnit } from '../AdUnit';
import { JumpToRecipe } from '../JumpToRecipe';
import { BlogInteractionHandler } from '../BlogInteractionHandler';
import { SaaSCallToAction } from '../SaaSCallToAction';
import OptimizedImage from '../OptimizedImage';


interface BlogPostPageProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
    post: BlogPost;
    handleNavigate: (path: string) => void;
}

const NavCard: React.FC<{ post: BlogPost; direction: 'Previous' | 'Next' }> = ({ post, direction }) => {
    const href = post.type === 'recipe' ? `/recipes/${post.slug}` : `/blog/${post.slug}`;
    const cleanTitle = (title: string) => title.split('|')[0].trim();

    return (
        <Link
            href={href}
            className="p-4 border border-brand-border rounded-lg hover:bg-brand-pink-light/50 transition-colors group"
        >
            <div className="text-sm font-bold text-brand-pink">{direction} {post.type === 'recipe' ? 'Recipe' : 'Article'}</div>
            <div className="font-semibold text-brand-text-title group-hover:underline">{cleanTitle(post.title)}</div>
        </Link>
    );
};

export const BlogPostPage: React.FC<BlogPostPageProps> = ({ setActiveTool, breadcrumbs, post, handleNavigate }) => {
    const { headings, contentWithIds } = generateToc(post.content);

    const currentIndex = blogPosts.findIndex(p => p.id === post.id);
    const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
    const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

    const postUrl = post.type === 'recipe' ? `https://sweetysbakery.com/recipes/${post.slug}` : `https://sweetysbakery.com/blog/${post.slug}`;

    return (
        <React.Fragment>
            <BlogInteractionHandler setActiveTool={setActiveTool} />
            {post.keywords && (
                <head>
                    <meta name="keywords" content={post.keywords} />
                </head>
            )}
            <ToolContainer title="" description="" setActiveTool={setActiveTool} breadcrumbs={breadcrumbs} toolId="baking-blog">
                {/* Modern Food Blog Header */}
                <div className="max-w-6xl mx-auto px-4 md:px-8 pt-12 pb-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                        {/* Left Column: Title & Meta */}
                        <div className="lg:col-span-2 space-y-6 text-left">
                            <h1 className="font-serif text-4xl md:text-5xl lg:text-5xl font-bold text-brand-text-title leading-tight">
                                {post.title}
                            </h1>

                            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-brand-text-body/80 border-y border-brand-pink/10 py-4">
                                <span className="flex items-center gap-2">
                                    <span className="text-brand-text-body/70">By</span>
                                    <span className="text-brand-pink font-bold uppercase tracking-wider">{post.author}</span>
                                </span>
                                <span className="w-1 h-1 rounded-full bg-brand-pink/20" />
                                <span className="flex items-center gap-2">
                                    <span className="text-brand-text-body/70">Published:</span>
                                    <time className="text-brand-text-title">{post.date}</time>
                                </span>
                                {post.updatedDate && (
                                    <>
                                        <span className="w-1 h-1 rounded-full bg-brand-pink/20" />
                                        <span className="flex items-center gap-2">
                                            <span className="text-brand-text-body/70">Updated:</span>
                                            <time className="text-brand-text-title">{post.updatedDate}</time>
                                        </span>
                                    </>
                                )}
                                <span className="w-1 h-1 rounded-full bg-brand-pink/20" />
                                <span className="flex items-center gap-2 text-brand-text-title">
                                    <ClockIcon className="w-4 h-4" />
                                    {post.readTime} read
                                </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-6">
                                {post.recipeData?.rating && (
                                    <div className="flex items-center gap-3 bg-brand-pink/5 px-4 py-2 rounded-full border border-brand-pink/10">
                                        <RecipeRating 
                                            slug={post.slug} 
                                            initialRating={post.recipeData.rating.ratingValue} 
                                            initialCount={post.recipeData.rating.ratingCount} 
                                        />
                                    </div>
                                )}

                                <div className="flex items-center gap-3">
                                    {post.type === 'recipe' && (
                                        <JumpToRecipe targetId="recipe-card" />
                                    )}

                                    <button 
                                        onClick={undefined} // Handled by print logic globally or via handler
                                        className="bg-white text-brand-text-title border-2 border-brand-pink/20 px-6 py-2 rounded-full font-bold text-sm tracking-wide hover:bg-brand-pink/5 transition-all flex items-center gap-2 uppercase"
                                    >
                                        <PrintIcon className="w-4 h-4" />
                                        Print
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs italic text-brand-text-body/80">
                                This post may contain affiliate links. Please read my <a href="/privacy-policy" className="underline hover:text-brand-pink">disclosure policy</a>.
                            </p>
                        </div>

                        {/* Right Column: Author Profile Short */}
                        <div className="hidden lg:flex flex-col items-center text-center p-8 bg-brand-pink-light/20 rounded-2xl border border-brand-pink/10 space-y-4">
                            <div className="relative">
                                <div className="absolute inset-0 bg-brand-pink rounded-full blur-lg opacity-20 animate-pulse" />
                                <Image
                                    src="/images/sarah-profile.jpg"
                                    alt={post.author}
                                    width={128}
                                    height={128}
                                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl relative z-10"
                                />
                            </div>
                            <div>
                                <h2 className="font-serif text-xl font-bold text-brand-text-title uppercase tracking-wider">Hi, I'm Sarah!</h2>
                                <p className="text-sm text-brand-text-body/70 mt-2 leading-relaxed italic">
                                    "I love sharing simplified recipes and professional tools to help you bake with confidence."
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <a href="https://www.instagram.com/sweetys.bakery/" className="p-2 bg-white text-brand-pink rounded-full shadow-sm hover:scale-110 transition-transform border border-brand-pink/10">
                                    <InstagramIcon className="w-4 h-4" />
                                </a>
                                <a href="https://www.pinterest.com/Sweetysbakerys/" className="p-2 bg-white text-brand-pink rounded-full shadow-sm hover:scale-110 transition-transform border border-brand-pink/10">
                                    <PinterestIcon className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
                    {/* Featured Image - relocated below header content */}
                    <div className="mb-12 relative group h-[400px] md:h-[600px] w-full rounded-3xl overflow-hidden shadow-2xl">
                        <div className="absolute inset-0 bg-brand-pink/5 rounded-3xl blur-2xl group-hover:bg-brand-pink/10 transition-colors z-0" />
                        <OptimizedImage
                            src={post.imageUrl}
                            alt={post.imageAlt || post.title}
                            title={post.imageTitle || post.title}
                            fill
                            priority
                            sizes="(max-width: 1200px) 100vw, 1200px"
                            containerClassName="w-full h-full absolute inset-0 z-10"
                            className="object-cover"
                        />
                        <PinItButton 
                            url={postUrl}
                            media={`https://sweetysbakery.com${post.imageUrl}`}
                            description={post.title}
                            className="absolute top-6 left-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 scale-110"
                        />
                    </div>
                </div>

                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 px-6 sm:px-8 md:px-10">
                    <article className="flex-1 min-w-0">
                        {/* Ads Leaderboard */}
                        <div className="my-8 flex justify-center w-full">
                            <AdUnit
                                slot="1376752077"
                                width={728}
                                height={90}
                                format="auto"
                                responsive={true}
                                className="my-2"
                            />
                        </div>

                        {/* Editorial Excerpt */}
                        <div className="mb-12 relative px-8 py-6 bg-brand-pink-light/10 rounded-2xl border-l-4 border-brand-pink">
                            <span className="absolute top-0 left-0 -mt-4 -ml-2 text-6xl text-brand-pink/20 font-serif leading-none">“</span>
                            <p className="text-xl md:text-2xl font-serif text-brand-text-title/90 italic leading-relaxed">
                                {post.excerpt}
                            </p>
                        </div>

                        <div className="mb-12 flex justify-between items-center py-6 border-t border-b border-brand-border/50">
                            <span className="font-bold text-sm text-brand-text-title uppercase tracking-wider">Share this article</span>
                            <ShareButtons 
                                url={postUrl}
                                imageUrl={`https://sweetysbakery.com${post.imageUrl}`}
                                description={post.excerpt}
                            />
                        </div>

                        {/* Mobile TOC */}
                        <div className="lg:hidden mb-12 bg-white rounded-xl p-6 border border-brand-border shadow-sm" id="toc-container">
                            <h2 className="font-serif text-xl font-bold text-brand-text-title mb-4 flex items-center gap-2">
                                <span className="text-brand-pink">📑</span> Table of Contents
                            </h2>
                            <TableOfContents headings={headings} />
                        </div>

                        {/* Article Content */}
                        <div id="blog-content" className="prose prose-lg prose-slate max-w-none 
                            prose-headings:font-serif prose-headings:font-bold prose-headings:text-brand-text-title prose-headings:tracking-tight
                            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-brand-pink/20 prose-h2:pb-2
                            prose-h3:text-2xl prose-h3:mt-8 prose-h3:text-brand-pink-dark
                            prose-p:leading-8 prose-p:text-brand-text-body/90 prose-p:mb-6
                            prose-a:text-brand-pink prose-a:font-semibold prose-a:no-underline prose-a:border-b-2 prose-a:border-brand-pink/30 hover:prose-a:border-brand-pink hover:prose-a:text-brand-pink-dark transition-colors
                            prose-blockquote:border-l-brand-pink prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:italic
                            prose-img:rounded-2xl prose-img:shadow-lg prose-img:my-10
                            prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-4 prose-ul:my-6
                            prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-4 prose-ol:my-6
                            prose-li:marker:text-brand-pink prose-li:text-brand-text-body/90 prose-li:leading-relaxed
                            [&>ul>li>strong]:text-brand-text-title [&>ol>li>strong]:text-brand-text-title
                            ">
                            {/* Mid-content ad — before the main body */}
                            <div className="my-8 not-prose flex justify-center w-full">
                                <AdUnit
                                    slot="4784663984"
                                    width={336}
                                    height={280}
                                    format="auto"
                                    responsive={true}
                                />
                            </div>

                            {/* Full article content rendered as one block — avoids hydration mismatch from paragraph splitting */}
                                <div
                                    id={post.type === 'recipe' ? 'recipe-card' : undefined}
                                    dangerouslySetInnerHTML={{ __html: contentWithIds }}
                                />

                                {/* SaaS CTA - Linking to the Mother Page */}
                                <SaaSCallToAction />

                            {/* Mid-content promo for mobile */}
                            <div className="lg:hidden not-prose my-10 w-full">
                                <PremiumPromo variant="inline-tool" />
                            </div>
                        </div>

                    </article>

                    {/* Sidebar */}
                    <aside className="hidden lg:block w-[320px] xl:w-[350px] flex-shrink-0">
                        <div className="sticky top-28 space-y-8">
                            <div className={`bg-white p-6 rounded-2xl border border-brand-border/60 shadow-sm max-h-[50vh] overflow-y-auto ${styles.customScrollbar}`}>
                                <h2 className="font-serif text-lg font-bold text-brand-text-title mb-4 pb-3 border-b border-brand-border flex items-center gap-2">
                                    <span>📑</span> Contents
                                </h2>
                                <div id="toc-container-desktop">
                                    <TableOfContents headings={headings} />
                                </div>
                            </div>

                            <PremiumPromo variant="sidebar" />

                            <div className="bg-white rounded-2xl overflow-hidden border border-brand-border shadow-sm">
                                <span className="block text-[10px] text-gray-400 text-center uppercase tracking-widest py-2 bg-gray-50 border-b">Advertisement</span>
                                <div className="flex justify-center p-4">
                                     <AdUnit
                                        slot="3105168656"
                                        width={300}
                                        height={600}
                                        format="auto"
                                        responsive={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>

                <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-10 mt-24 pt-12 border-t border-brand-border/40 space-y-20">
                    <div className="bg-white rounded-3xl p-8 md:p-10 border border-brand-border shadow-sm">
                        <AuthorBio handleNavigate={handleNavigate} />
                    </div>

                    <div>
                        <h2 className="font-serif text-3xl font-bold text-brand-text-title text-center mb-8">Get in Touch</h2>
                        <ContactForm />
                    </div>

                    {(prevPost || nextPost) && (
                        <div className="pt-10 border-t border-brand-border/40">
                            <h2 className="font-serif text-2xl font-bold text-brand-text-title mb-8">Continue Reading</h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                {prevPost && <NavCard post={prevPost} direction="Previous" />}
                                {nextPost && <NavCard post={nextPost} direction="Next" />}
                            </div>
                        </div>
                    )}
                </div>
            </ToolContainer>
        </React.Fragment>
    );
};
